/**
 * Preview invoke bridge (task 1071).
 *
 * **Why it exists.** `PreviewSandbox` renders generated source in an opaque
 * (null-origin) frame — `allow-scripts` without `allow-same-origin` — so the
 * page holds no session. Its `fetch(window.__INVOKE_URL__)` therefore reached
 * innovate-service with no User claims, was classed a `billable` (outside the
 * app) session, and was refused by the `api_access` gate on the Free plan, even
 * though the User was running their own AI Service inside the app. The Testing
 * tab never hit this because it calls through the app's axios `api` instance.
 *
 * **What it does.** The frame's bootstrap replaces `window.fetch` with a shim
 * (`buildInvokeBridgeShim`). A request whose URL is under `window.__API_URL__`
 * is serialized (method, URL, headers, body as text) and posted to the parent
 * with a correlation id; every other URL goes to the real `fetch` untouched.
 * The parent (`handleBridgeRequest`) answers only its own frame, only for the
 * previewed endpoint's invoke URL or that endpoint call's polling handle,
 * performs the request with the app's authenticated axios `api` instance (owner
 * JWT, trusted app Origin) and posts back status, headers and body as text.
 * innovate-service then classes the call `owner`, exactly like the Testing tab.
 *
 * **Why the sandbox is not widened.** The frame still has no same-origin access:
 * it cannot read the token, cookies, storage or DOM of the app. All it can do is
 * ASK the parent to make one of two kinds of call, and the parent decides. That
 * allow-list is deliberately narrower than "anything under the API base": the
 * owner's JWT is good for every management route, and generated code (or
 * content it renders) must never be able to reach those through the bridge.
 *
 * Only the in-app preview receives the bridge. Deployed interfaces (served by
 * innovate-service `controllers/endpointUi.ts`), the Developer Info snippets
 * (`serviceSnippets.ts`) and copied-out source (the UI Composer copies the raw
 * `source`, never the srcDoc) carry no shim, so their calls stay outside-the-app
 * calls, classed and gated as before.
 */
import api from '@/services/api';
import config from '@/config';

/** Message `type` the frame posts to ask the parent to perform a request. */
export const BRIDGE_REQUEST_TYPE = 'invoke-request';
/** Message `type` the parent posts back with the outcome. */
export const BRIDGE_RESPONSE_TYPE = 'invoke-response';

/**
 * Headers never forwarded from the frame. The owner's JWT is the only credential
 * the bridge attaches; a header the page supplies must not replace it or add a
 * second identity (an `x-api-key` would turn the call back into an API-key call).
 */
const STRIPPED_REQUEST_HEADERS = new Set([
  'authorization',
  'x-api-key',
  'cookie',
  'origin',
  'host',
  'content-length',
]);

/** Statuses a `Response` may not carry a body for. */
const NULL_BODY_STATUSES = [204, 205, 304];

/** One request as the frame serialized it. */
interface BridgeRequest {
  id: string;
  url: string;
  method: string;
  headers: [string, string][];
  body: string | null;
}

/** What the parent posts back to the frame under the request's id. */
type BridgeReply =
  | {
      id: string;
      ok: true;
      status: number;
      statusText: string;
      headers: [string, string][];
      body: string;
    }
  | { id: string; ok: false; error: string };

function apiBase(): URL | null {
  try {
    return new URL(config.API_URL);
  } catch {
    return null;
  }
}

function trimSlash(path: string): string {
  return path.replace(/\/+$/, '');
}

/**
 * May the bridge perform this request as the owner?
 *
 * Only two shapes, both on the configured API origin and under its base path:
 *   1. the previewed endpoint's own invoke URL (any method — the endpoint
 *      decides which it accepts), query string allowed;
 *   2. a `GET` of an async polling handle,
 *      `/api/endpoints/<endpointId>/requests/<requestId>/responses[/<responseId>]`,
 *      which is what an async invoke returns in `data.polling.url`.
 * Everything else under the API — every management route — is refused.
 */
export function isBridgeUrlAllowed(
  url: string,
  method: string,
  invokeUrl: string | null
): boolean {
  const base = apiBase();
  if (!base || !invokeUrl) return false;

  let target: URL;
  let invoke: URL;
  try {
    target = new URL(url);
    invoke = new URL(invokeUrl);
  } catch {
    return false;
  }

  if (target.username || target.password) return false;
  if (target.origin !== base.origin) return false;

  const basePath = trimSlash(base.pathname);
  if (target.pathname !== basePath && !target.pathname.startsWith(`${basePath}/`)) return false;

  const targetPath = trimSlash(target.pathname);
  if (invoke.origin === target.origin && targetPath === trimSlash(invoke.pathname)) {
    return true;
  }

  const relative = targetPath.slice(basePath.length);
  return (
    method.toUpperCase() === 'GET' &&
    /^\/api\/endpoints\/[^/]+\/requests\/[^/]+\/responses(\/[^/]+)?$/.test(relative)
  );
}

/** Narrow an untrusted message payload to a well-formed bridge request. */
export function parseBridgeRequest(data: unknown): BridgeRequest | null {
  if (!data || typeof data !== 'object') return null;
  const candidate = data as Record<string, unknown>;
  if (
    typeof candidate.id !== 'string' ||
    typeof candidate.url !== 'string' ||
    typeof candidate.method !== 'string' ||
    !(candidate.body === null || typeof candidate.body === 'string') ||
    !Array.isArray(candidate.headers)
  ) {
    return null;
  }
  const headers = candidate.headers.filter(
    (pair): pair is [string, string] =>
      Array.isArray(pair) &&
      pair.length === 2 &&
      typeof pair[0] === 'string' &&
      typeof pair[1] === 'string'
  );
  return {
    id: candidate.id,
    url: candidate.url,
    method: candidate.method,
    headers,
    body: candidate.body as string | null,
  };
}

/**
 * Perform an allowed request as the signed-in owner and describe the outcome.
 *
 * The axios `api` instance attaches the owner's JWT and, being the app's own
 * page, the browser sends the trusted app Origin. The body comes back as raw
 * text (no JSON transform) so the frame's `Response` reads exactly what the
 * service sent. Every status except 401 resolves, so the page sees the service's
 * own answer; a 401 still takes the instance's silent-refresh path first.
 */
async function relay(request: BridgeRequest): Promise<BridgeReply> {
  const headers: Record<string, string> = {};
  for (const [name, value] of request.headers) {
    if (!STRIPPED_REQUEST_HEADERS.has(name.toLowerCase())) headers[name] = value;
  }

  const method = request.method.toUpperCase();
  try {
    const response = await api.request<string>({
      url: request.url,
      method,
      headers,
      data: method === 'GET' || method === 'HEAD' ? undefined : (request.body ?? undefined),
      responseType: 'text',
      transformResponse: [(raw: unknown) => raw],
      validateStatus: (status) => status !== 401,
    });

    const responseHeaders: [string, string][] = [];
    for (const [name, value] of Object.entries(response.headers ?? {})) {
      if (value === undefined || value === null) continue;
      responseHeaders.push([name, Array.isArray(value) ? value.join(', ') : String(value)]);
    }

    return {
      id: request.id,
      ok: true,
      status: response.status,
      statusText: response.statusText ?? '',
      headers: responseHeaders,
      body: NULL_BODY_STATUSES.includes(response.status)
        ? ''
        : typeof response.data === 'string'
          ? response.data
          : JSON.stringify(response.data ?? ''),
    };
  } catch (error) {
    // Only a 401 the refresh could not recover, or a network failure, lands
    // here. A status the service answered is still the page's to read.
    const failure = error as { status?: number; response?: unknown; message?: string };
    if (typeof failure.status === 'number') {
      return {
        id: request.id,
        ok: true,
        status: failure.status,
        statusText: '',
        headers: [],
        body:
          typeof failure.response === 'string'
            ? failure.response
            : JSON.stringify(failure.response ?? ''),
      };
    }
    return { id: request.id, ok: false, error: failure.message || 'Request failed' };
  }
}

/**
 * Answer one bridge request from the frame. The caller has already proved the
 * sender is its own frame (object identity); this validates the payload and the
 * URL, relays, and posts the reply back to that frame only. A refused request
 * is logged and answered with `ok: false`, so the page's `fetch` rejects the
 * way a blocked network call would instead of hanging.
 */
export async function handleBridgeRequest(
  data: unknown,
  frameWindow: Window,
  invokeUrl: string | null,
  messageSource: string
): Promise<void> {
  const request = parseBridgeRequest(data);
  if (!request) {
    console.warn('PreviewSandbox: ignored a malformed preview bridge request');
    return;
  }

  let reply: BridgeReply;
  if (!isBridgeUrlAllowed(request.url, request.method, invokeUrl)) {
    console.warn('PreviewSandbox: refused a preview bridge request outside the allow-list', {
      method: request.method,
      url: request.url,
    });
    reply = { id: request.id, ok: false, error: 'Request not permitted from the preview' };
  } else {
    reply = await relay(request);
  }

  try {
    // The frame is a null origin, so '*' is the only target origin that reaches
    // it; the recipient is pinned by holding its window, not by origin.
    frameWindow.postMessage({ source: messageSource, type: BRIDGE_RESPONSE_TYPE, ...reply }, '*');
  } catch {
    /* frame went away — nobody left to answer */
  }
}

/**
 * The frame half: a `fetch` shim, as script text for the srcDoc bootstrap.
 * Expects `boot` (with `apiUrl` and `messageSource`) in scope.
 *
 * Intercepts only URLs under `boot.apiUrl` (which covers `__INVOKE_URL__` and
 * `__API_URL__ + data.polling.url`); everything else, and any input `Request`
 * cannot parse, goes to the real `fetch`. Replies are accepted only from
 * `window.parent` and only for an id this frame issued.
 */
export function buildInvokeBridgeShim(): string {
  return `
  (function installInvokeBridge(){
    var realFetch = window.fetch ? window.fetch.bind(window) : null;
    var base = String(boot.apiUrl || '').replace(/\\/+$/, '');
    if (!base || !realFetch || typeof Request !== 'function' || typeof Response !== 'function') return;
    var pending = {};
    var seq = 0;
    window.addEventListener('message', function(event){
      if (event.source !== window.parent) return;
      var d = event.data;
      if (!d || d.source !== boot.messageSource || d.type !== '${BRIDGE_RESPONSE_TYPE}' || typeof d.id !== 'string') return;
      var entry = pending[d.id];
      if (!entry) return;
      delete pending[d.id];
      if (!d.ok) { entry.reject(new TypeError(d.error || 'Request failed')); return; }
      var nullBody = d.status === 204 || d.status === 205 || d.status === 304;
      try {
        entry.resolve(new Response(nullBody ? null : d.body, {
          status: d.status, statusText: d.statusText || '', headers: d.headers || []
        }));
      } catch (e) { entry.reject(new TypeError('Response could not be read')); }
    });
    window.fetch = function(input, init){
      var request;
      try { request = new Request(input, init); } catch (e) { return realFetch(input, init); }
      var url = request.url;
      if (url !== base && url.indexOf(base + '/') !== 0) return realFetch(input, init);
      var method = request.method;
      var headers = [];
      request.headers.forEach(function(value, name){ headers.push([name, value]); });
      var bodyRead = (method === 'GET' || method === 'HEAD') ? Promise.resolve(null) : request.text();
      return bodyRead.then(function(body){
        return new Promise(function(resolve, reject){
          var signal = request.signal;
          if (signal && signal.aborted) { reject(new DOMException('Aborted', 'AbortError')); return; }
          var id = 'bridge-' + (++seq) + '-' + Math.random().toString(36).slice(2);
          pending[id] = { resolve: resolve, reject: reject };
          if (signal) {
            signal.addEventListener('abort', function(){
              if (pending[id]) { delete pending[id]; reject(new DOMException('Aborted', 'AbortError')); }
            });
          }
          window.parent.postMessage({
            source: boot.messageSource, type: '${BRIDGE_REQUEST_TYPE}',
            id: id, url: url, method: method, headers: headers, body: body
          }, '*');
        });
      });
    };
  })();`;
}
